// Shared API helpers for the KSP Training tile
const API_URL = process.env.REACT_APP_API_URL ||
  (window.location.hostname === 'localhost' ? 'http://localhost:5000/api' : '/api');

const authHeaders = (extra = {}) => {
  const token = localStorage.getItem('token');
  return {
    Authorization: token ? `Bearer ${token}` : '',
    ...extra,
  };
};

const handle = async (res) => {
  if (!res.ok) {
    let detail = '';
    let body = null;
    try { body = await res.json(); detail = body.message || body.details || ''; } catch (_) {}
    const err = new Error(detail || `Request failed: ${res.status}`);
    err.status = res.status;
    err.body = body;
    throw err;
  }
  if (res.status === 204) return null;
  return res.json();
};

/** ?lang=xx, omitted when no language is given so the server picks the course default. */
const langQuery = (language, separator = '?') =>
  (language ? `${separator}lang=${encodeURIComponent(language)}` : '');

export const trainingApi = {
  // User
  listTrainings: (language) =>
    fetch(`${API_URL}/training${langQuery(language)}`, { headers: authHeaders() }).then(handle),
  getTraining: (id, language) =>
    fetch(`${API_URL}/training/${id}${langQuery(language)}`, { headers: authHeaders() }).then(handle),
  startAttempt: (id, language) =>
    fetch(`${API_URL}/training/${id}/attempts`, {
      method: 'POST',
      headers: authHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({ language }),
    }).then(handle),
  submitAttempt: (attemptId, answers) =>
    fetch(`${API_URL}/training/attempts/${attemptId}/submit`, {
      method: 'POST',
      headers: authHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({ answers }),
    }).then(handle),
  myHistory: (language) =>
    fetch(`${API_URL}/training/me/history${langQuery(language)}`, { headers: authHeaders() }).then(handle),
  listLanguages: () =>
    fetch(`${API_URL}/training/languages`, { headers: authHeaders() }).then(handle),
  setMyLanguage: (language) =>
    fetch(`${API_URL}/training/me/language`, {
      method: 'PUT',
      headers: authHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({ language }),
    }).then(handle),

  // Admin
  adminListAll: () =>
    fetch(`${API_URL}/training/admin/all`, { headers: authHeaders() }).then(handle),
  adminGet: (id) =>
    fetch(`${API_URL}/training/admin/${id}`, { headers: authHeaders() }).then(handle),
  adminCreate: (data) =>
    fetch(`${API_URL}/training/admin`, {
      method: 'POST',
      headers: authHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify(data),
    }).then(handle),
  adminUpdate: (id, data) =>
    fetch(`${API_URL}/training/admin/${id}`, {
      method: 'PUT',
      headers: authHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify(data),
    }).then(handle),
  adminDelete: (id) =>
    fetch(`${API_URL}/training/admin/${id}`, {
      method: 'DELETE',
      headers: authHeaders(),
    }).then(handle),
  adminUploadPptx: (id, file, onProgress, language) => {
    const form = new FormData();
    form.append('pptxFile', file);
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', `${API_URL}/training/admin/${id}/pptx${langQuery(language)}`);
      const token = localStorage.getItem('token');
      if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable && onProgress) onProgress(Math.round((e.loaded / e.total) * 100));
      };
      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          try { resolve(JSON.parse(xhr.responseText)); } catch (_) { resolve({}); }
        } else {
          let msg = `Upload failed: ${xhr.status}`;
          try { const j = JSON.parse(xhr.responseText); msg = j.message || j.details || msg; } catch (_) {}
          reject(new Error(msg));
        }
      };
      xhr.onerror = () => reject(new Error('Network error during upload'));
      xhr.send(form);
    });
  },
  adminUpsertQuestion: (trainingId, payload) =>
    fetch(`${API_URL}/training/admin/${trainingId}/questions`, {
      method: 'POST',
      headers: authHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify(payload),
    }).then(handle),
  adminDeleteQuestion: (qid) =>
    fetch(`${API_URL}/training/admin/questions/${qid}`, {
      method: 'DELETE',
      headers: authHeaders(),
    }).then(handle),
  // discardTranslations must be set once the admin has confirmed that replacing the
  // English questions also destroys the Spanish/Hmong text (server returns 409 first).
  adminImportQuestions: (trainingId, questions, discardTranslations = false) =>
    fetch(`${API_URL}/training/admin/${trainingId}/questions/import`, {
      method: 'POST',
      headers: authHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({ questions, discard_translations: discardTranslations }),
    }).then(handle),

  // Admin — languages & translations
  adminSaveTranslations: (trainingId, payload) =>
    fetch(`${API_URL}/training/admin/${trainingId}/translations`, {
      method: 'POST',
      headers: authHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify(payload),
    }).then(handle),
  adminSetLanguageAvailability: (trainingId, language, isAvailable) =>
    fetch(`${API_URL}/training/admin/${trainingId}/languages/${language}`, {
      method: 'PUT',
      headers: authHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({ is_available: !!isAvailable }),
    }).then(handle),
  adminDeleteLanguage: (trainingId, language) =>
    fetch(`${API_URL}/training/admin/${trainingId}/languages/${language}`, {
      method: 'DELETE',
      headers: authHeaders(),
    }).then(handle),

  // Admin — metrics
  adminMetricsMatrix: () =>
    fetch(`${API_URL}/training/admin/metrics/matrix`, { headers: authHeaders() }).then(handle),
  adminMetricsCompletions: () =>
    fetch(`${API_URL}/training/admin/metrics/completions`, { headers: authHeaders() }).then(handle),
  adminMetricsLanguages: () =>
    fetch(`${API_URL}/training/admin/metrics/languages`, { headers: authHeaders() }).then(handle),
  adminTrainingDetail: (id) =>
    fetch(`${API_URL}/training/admin/metrics/training/${id}`, { headers: authHeaders() }).then(handle),
  adminUserDetail: (userId) =>
    fetch(`${API_URL}/training/admin/metrics/user/${userId}`, { headers: authHeaders() }).then(handle),
  adminRecentActivity: (limit = 20) =>
    fetch(`${API_URL}/training/admin/metrics/recent?limit=${limit}`, { headers: authHeaders() }).then(handle),

  // Helpers
  slideUrl: (id, n, language) =>
    `${API_URL}/training/${id}/slides/${n}${langQuery(language)}`,
};

export default trainingApi;
